import React, { useCallback } from 'react';
import cn from 'classnames';

import ICO_BOOK from 'assets/images/icons/book-open-black.svg';
import Button from 'components/form/Button.component';
import Note from 'components/form/Note.component';
import Foldout from 'components/generic/Foldout.component';
import OverlaySpinner from 'components/generic/OverlaySpinner.component';
import useAfn from 'hooks/useAfn.hook';
import useResettingState from 'hooks/useResettingState.hook';
import useTip from 'hooks/useTip.hook';
import { useTranslation } from 'providers/GILT.provider';
import { useNcobSettings } from 'providers/NcobSettings.provider';
import { useTheme } from 'providers/Theme.provider';
import fromAnyToString from 'utils/converters/fromAnyToString';
import logger from 'utils/logger.util';
import asObject from 'utils/objects/asObject.util';

import CN from './WelcomePanel.module.scss';

const L = logger('WelcomePanel');

const WelcomePanel = () => {
  const { isDarkMode } = useTheme();
  const { t } = useTranslation();
  const { value, isLoading, error, update } = useNcobSettings();
  const { salonNcobWelcomeMessage } = value ?? {};

  const [message, setWelcomeMessage] = useResettingState(salonNcobWelcomeMessage, useResettingState.NIL);
  const { val: isDirty, inv: isPristine, yay: markDirty, nay: markPristine } = useTip(false);

  const isEmpty = fromAnyToString(message).length === 0;
  const isHidden = isPristine || isEmpty;
  const isDisabled = isPristine || isEmpty;

  const [, handleSaveWelcomeText] = useAfn(
    async ({ raw }) => {
      if (raw === null) return;
      const { ok, value } = await update({ data: { salonNcobWelcomeMessage: message } });
      if (!ok) return;
      setWelcomeMessage(fromAnyToString(asObject(value).salonNcobWelcomeMessage));
      markPristine();
    },
    [markPristine, setWelcomeMessage, message, update],
  );

  const handleWelcomeChange = useCallback(
    (ev) => {
      setWelcomeMessage(fromAnyToString(ev?.raw));
      markDirty();
    },
    [setWelcomeMessage, markDirty],
  );

  if (error) L.debug('()', { isLoading, isDirty, message, error, value });

  return (
    <div
      data-bem="WelcomePanel"
      className={cn({
        [CN.component]: true,
        [CN.darkMode]: isDarkMode,
      })}
    >
      {isLoading && <OverlaySpinner />}

      <Foldout tag="WelcomePanel" src={ICO_BOOK} title={t('onlineBooking.policyAndTerms.welcomeMessage.title')}>
        <div data-bem="WelcomePanel__container" className={CN.container}>
          <div className={CN.message}>
            <div className={CN.content}>
              <div className={CN.sectionTitle}>{t('onlineBooking.policyAndTerms.welcomeMessage.title')}</div>
              <div className={CN.text}>{t('onlineBooking.policyAndTerms.welcomeMessage.description')}</div>
            </div>
            <div className={CN.imageMessage} />
          </div>

          <div data-bem="WelcomePanel__note">
            <Note autoscroll hasExternalValue name="note" onChange={handleWelcomeChange} tag="WelcomePanel__note" value={message} variant="expanded" />
            <div
              className={cn({
                [CN.buttonContainer]: true,
                [CN.hidden]: isHidden,
              })}
            >
              <Button variant="primary" onClick={handleSaveWelcomeText} disabled={isDisabled}>
                {isDirty ? t('common.buttons.saveUpp') : t('common.buttons.editUpp')}
              </Button>
            </div>
          </div>
        </div>
      </Foldout>
    </div>
  );
};

export default WelcomePanel;
